import { ComposeValidation, createCompose } from './compose';
import { createCustom, CustomValidation } from './custom';
import { createMatch, MatchValidation } from './match';
import { createRequired, RequiredValidation } from './required';
import { createResults } from './results';

export interface IValidationResult {
  id?: string;
  valid: boolean;
  message: string;
}

enum Validator {
  REQUIRED = 'required',
  MATCH = 'match',
  CUSTOM = 'custom',
  COMPOSE = 'compose',
  RESULTS = 'results'
}

export type Validation<T> = {
  [Validator.REQUIRED]: RequiredValidation<T>;
  [Validator.MATCH]: MatchValidation<T>;
  [Validator.CUSTOM]: CustomValidation<T>;
  [Validator.COMPOSE]: ComposeValidation<T>;
  [Validator.RESULTS]: (value: T) => Promise<IValidationResult[]>;
};

export type ValidationHandler<T> = (value: T) => Promise<IValidationResult[]>;

export const getValidations = <T>(chain: ValidationHandler<T>[]): Validation<T> => ({
  [Validator.REQUIRED]: createRequired<T>(chain),
  [Validator.MATCH]: createMatch<T>(chain),
  [Validator.CUSTOM]: createCustom<T>(chain),
  [Validator.COMPOSE]: createCompose<T>(chain),
  [Validator.RESULTS]: async (value: T) => createResults(value, chain)
});

export namespace Validators {
  export const required = <T = any>(message: string, id?: string) => getValidations<T>([])[Validator.REQUIRED](message, id);
  export const match = <T = any>(regex: RegExp, message: string, id?: string) => getValidations<T>([])[Validator.MATCH](regex, message, id);
  export const custom = <T = any>(handler: (value: T) => boolean, message: string, id?: string) =>
    getValidations<T>([])[Validator.CUSTOM](handler, message, id);
  export const compose = <T = any>(validations: Validation<T>[]) => getValidations<T>([])[Validator.COMPOSE](validations);
}

export const createValidations = <T>(chain: ValidationHandler<T>[]): Validation<T> => ({ ...getValidations(chain) });
